/**
 * SKU Parser for multi-pack variant detection.
 *
 * Identifies relationships between base variants (e.g., RETA-10MG-1VIAL)
 * and their multi-pack counterparts (e.g., RETA-10MG-1VIAL-2PK).
 */

const MULTI_PACK_REGEX = /^(.+)-(\d+)PK$/;

// --- Types ---

export interface ParsedSku {
	baseSku: string;
	packCount: number;
	isMultiPack: boolean;
}

interface SyncVariantStock {
	id: string;
	warehouse: { id: string };
	quantity: number;
	quantityAllocated: number;
}

interface SyncVariantChannelListing {
	channel: { slug: string };
	price: { amount: number; currency: string } | null;
	costPrice: { amount: number; currency: string } | null;
}

export interface SyncVariant {
	id: string;
	name: string;
	sku: string | null;
	trackInventory: boolean;
	stocks: SyncVariantStock[];
	channelListings: SyncVariantChannelListing[];
}

export interface VariantGroup {
	baseSku: string;
	base: SyncVariant | null;
	multiPacks: Array<{ variant: SyncVariant; packCount: number }>;
}

// --- Functions ---

/**
 * Parses a SKU string to identify if it's a multi-pack variant.
 *
 * SKUs ending in `-{N}PK` (e.g., `-2PK`, `-3PK`) are multi-packs.
 * SKUs without that suffix are base variants (packCount = 1).
 * Null/undefined/empty returns null.
 *
 * @example
 * parseSku("RETA-10MG-1VIAL")      // { baseSku: "RETA-10MG-1VIAL", packCount: 1, isMultiPack: false }
 * parseSku("RETA-10MG-1VIAL-2PK")  // { baseSku: "RETA-10MG-1VIAL", packCount: 2, isMultiPack: true }
 */
export function parseSku(sku: string): ParsedSku | null {
	if (!sku) {
		return null;
	}

	const match = sku.match(MULTI_PACK_REGEX);

	if (match) {
		return {
			baseSku: match[1],
			packCount: parseInt(match[2], 10),
			isMultiPack: true,
		};
	}

	return {
		baseSku: sku,
		packCount: 1,
		isMultiPack: false,
	};
}

/**
 * Groups an array of variants by their base SKU.
 *
 * Variants with null SKU are skipped. Multi-pack variants are sorted
 * by packCount ascending within each group.
 */
export function groupVariantsByBaseSku(variants: SyncVariant[]): Map<string, VariantGroup> {
	const groups = new Map<string, VariantGroup>();

	for (const variant of variants) {
		if (variant.sku === null) {
			continue;
		}

		const parsed = parseSku(variant.sku);
		if (!parsed) {
			continue;
		}

		const { baseSku, packCount, isMultiPack } = parsed;

		let group = groups.get(baseSku);
		if (!group) {
			group = {
				baseSku,
				base: null,
				multiPacks: [],
			};
			groups.set(baseSku, group);
		}

		if (isMultiPack) {
			group.multiPacks.push({ variant, packCount });
		} else {
			group.base = variant;
		}
	}

	// Sort multi-packs by packCount ascending
	for (const group of groups.values()) {
		group.multiPacks.sort((a, b) => a.packCount - b.packCount);
	}

	return groups;
}
